import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, type GenerationJob, type GenerationProject } from '../lib/api.js';
import { compatibleConnectors, produceCapability } from '../lib/studioGoal.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * The step after the prompt: turn it into the picture, the voice or the video.
 *
 * Shown once a media prompt is finished. The platform picks the connector by
 * itself; the select only lets a person insist on one of the tested ones that
 * suit this request. A refusal (a connector's longest clip, say) comes back as
 * a readable message and is shown as it is. The new job or plan then appears
 * among the project's results, where the existing cards and publish menu take
 * over.
 */
export function ProduceSection({ project, promptJob }: { project: GenerationProject; promptJob: GenerationJob }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [connectorId, setConnectorId] = useState('');
  const [started, setStarted] = useState(false);

  const capability = produceCapability(project.goal, project.followUp);

  const connectors = useQuery({
    queryKey: ['generation-connectors'],
    queryFn: api.listGenerationConnectors,
    enabled: capability !== null,
  });

  const produce = useMutation({
    mutationFn: () =>
      api.produceGenerationProject(project.id, {
        fromJobId: promptJob.id,
        ...(connectorId ? { connectorId } : {}),
      }),
    onSuccess: () => {
      setStarted(true);
      void queryClient.invalidateQueries({ queryKey: ['generation-project', project.id] });
      void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
    },
    onMutate: () => setStarted(false),
  });

  if (!capability) return null;

  const choices = compatibleConnectors(connectors.data?.connectors ?? [], project.goal, project.followUp);
  const label = t(`studio.produce.${capability}` as TranslationKey);

  return (
    <section className="card project-produce">
      <strong>{t('studio.produce.title')}</strong>
      <p className="muted small">{t('studio.produce.hint')}</p>

      {choices.length > 0 && (
        <div className="goal-field">
          <label htmlFor={`produce-connector-${project.id}`}>{t('studio.produce.connector')}</label>
          <select
            id={`produce-connector-${project.id}`}
            value={connectorId}
            onChange={(event) => setConnectorId(event.target.value)}
          >
            <option value="">{t('studio.produce.auto')}</option>
            {choices.map((connector) => (
              <option key={connector.id} value={connector.id}>
                {connector.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {capability === 'VIDEO' && <p className="warn-text small">{t('studio.produce.videoCost')}</p>}

      <div className="actions">
        <button type="button" className="primary" onClick={() => produce.mutate()} disabled={produce.isPending}>
          <Icon.sparkle />
          {produce.isPending ? t('studio.produce.working') : label}
        </button>
      </div>

      {produce.isError && <ErrorNotice error={produce.error} />}
      {started && !produce.isError && <p className="ok-text small">{t('studio.produce.started')}</p>}
    </section>
  );
}
